import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { Access } from '@/modules/common/enums/access.enum.ts';
import { Role } from '@/modules/common/enums/role.ts';
import { DocumentStatusId } from '@/modules/common/enums/document-status-id.enum.ts';
import { useAccessStore } from '@/modules/core/stores/access.store.ts';
import { useRolesStore } from '@/modules/core/stores/roles.store.ts';
import { useUserStore } from '@/modules/core/stores/user.store.ts';

export function useDocumentAccess(
  statusId: MaybeRefOrGetter<unknown>,
  controllerId: MaybeRefOrGetter<unknown> = () => undefined
) {
  const accessStore = useAccessStore();
  const rolesStore = useRolesStore();
  const userStore = useUserStore();

  const isAdmin = computed(() => rolesStore.has(Role.Administrator));
  const isClerk = computed(() => rolesStore.has(Role.Clerk));
  const isStopped = computed(() => toValue(statusId) === DocumentStatusId.Stopped);
  const hasDocumentsEdit = computed(() => accessStore.has(Access.DocumentsEdit));
  const isController = computed(() => {
    const id = toValue(controllerId);

    return id != null && id === userStore.id;
  });

  const canEdit = computed(() => hasDocumentsEdit.value && !isStopped.value);
  const canEditDocument = computed(
    () => !isStopped.value && (isAdmin.value || (isController.value && hasDocumentsEdit.value))
  );
  const canAddFiles = computed(() => hasDocumentsEdit.value && (!isStopped.value || isAdmin.value));
  const canDeleteFiles = computed(() => {
    const hasPrivilegedDelete = (isAdmin.value || isClerk.value) && accessStore.has(Access.DocumentsFilesDelete);

    if (toValue(statusId) === DocumentStatusId.Draft) {
      return isController.value || hasPrivilegedDelete;
    }

    return hasPrivilegedDelete;
  });

  return {
    canEdit,
    canEditDocument,
    canAddFiles,
    canDeleteFiles,
    hasDocumentsEdit,
    isAdmin,
  };
}
